import { Menu, MoonStar, SunMedium, X } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import brandLogo from "@/assets/aiv-logo-trans.png";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";

type HomeNavbarProps = {
  isDark: boolean;
  onToggleTheme: () => void;
};

export default function HomeNavbar({ isDark, onToggleTheme }: HomeNavbarProps) {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [activeHash, setActiveHash] = React.useState("");
  const links = React.useMemo(
    () => [
      { href: "#features", label: t("landing_nav_features", "Features") },
      { href: "#pricing", label: t("landing_nav_pricing", "Pricing") },
      { href: "#contact", label: t("landing_nav_contact", "Contact") },
    ],
    [t],
  );

  React.useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 30);

      const sections = links.map(link => link.href.substring(1));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element && scrollY >= element.offsetTop - 100) {
          setActiveHash(section);
          break;
        }
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.substring(1);
    const element = document.getElementById(targetId);

    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });

      setActiveHash(targetId);
      setOpen(false);
      window.history.pushState(null, "", href);
    }
  };

  // Dynamic styles based on scroll and dark mode
  const isTransparent = !scrolled;

  const headerBgClass = scrolled
    ? isDark
      ? "bg-gray-950/95 border-gray-800 shadow-lg backdrop-blur-xl"
      : "bg-white/95 border-gray-200 shadow-lg backdrop-blur-xl"
    : "bg-transparent border-transparent shadow-none";

  const textColorClass = isTransparent
    ? "text-white"
    : isDark
      ? "text-gray-300"
      : "text-gray-700";

  const mutedTextClass = isTransparent
    ? "text-white/70"
    : isDark
      ? "text-gray-500"
      : "text-gray-500";

  const navLinkClass = (isActive: boolean) => {
    if (isTransparent) {
      return isActive ? "text-[#20c5b5]" : "text-white/80 hover:text-white";
    }
    if (isDark) {
      return isActive ? "text-[#20c5b5]" : "text-gray-400 hover:text-white";
    }
    return isActive ? "text-[#20c5b5]" : "text-gray-600 hover:text-gray-900";
  };

  const buttonVariantClass = (type: "ghost" | "primary") => {
    if (type === "ghost") {
      if (isTransparent) {
        return "text-white hover:bg-white/10 hover:text-white";
      }
      if (isDark) {
        return "text-gray-300 hover:bg-gray-800 hover:text-white";
      }
      return "text-gray-700 hover:bg-gray-100 hover:text-gray-900";
    }

    // Primary button
    if (isTransparent) {
      return "bg-gradient-to-r from-[#20c5b5] to-[#ef4343] text-white hover:shadow-lg hover:scale-105";
    }
    if (isDark) {
      return "bg-gradient-to-r from-[#20c5b5] to-[#ef4343] text-white hover:shadow-lg hover:scale-105";
    }
    return "bg-gradient-to-r from-[#20c5b5] to-[#ef4343] text-white hover:shadow-lg hover:scale-105";
  };

  const mobileMenuClass = isDark
    ? "bg-gray-950/95 border-gray-800"
    : "bg-white/95 border-gray-200";

  const mobileLinkClass = (isActive: boolean) => {
    if (isDark) {
      return isActive
        ? "bg-[#20c5b5]/10 text-[#20c5b5]"
        : "text-gray-300 hover:bg-gray-800 hover:text-white";
    }
    return isActive
      ? "bg-[#20c5b5]/10 text-[#20c5b5]"
      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900";
  };

  const handleBrandClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.history.pushState(null, "", "/");
    setActiveHash("");
    setOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 z-50 w-full"
    >
      <div className="px-3 pt-3 sm:px-4 sm:pt-4 lg:px-6 lg:pt-5">
        <div className={`mx-auto flex max-w-7xl items-center justify-between rounded-xl border px-3 py-2 transition-all duration-500 sm:rounded-2xl sm:px-4 sm:py-2.5 ${headerBgClass}`}>

          {/* Logo */}
          <Link to="/" onClick={handleBrandClick} className="group flex items-center gap-2 sm:gap-2.5">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-300 sm:h-9 sm:w-9 lg:h-10 lg:w-10 ${isTransparent
                  ? "bg-white/10 backdrop-blur-sm group-hover:bg-white/20"
                  : isDark
                    ? "bg-gray-800 group-hover:bg-gray-700"
                    : "bg-gray-100 group-hover:bg-gray-200"
                }`}
            >
              <img
                src={brandLogo}
                alt="Smashlytics"
                className="h-8 w-8 object-contain sm:h-10 sm:w-10" />
            </motion.div>
            <div>
              <p className={`text-xs font-semibold tracking-tight transition-colors duration-300 sm:text-sm ${textColorClass}`}>
                Smashlytics
              </p>
              <p className={`hidden text-[8px] uppercase tracking-wider transition-colors duration-300 sm:block ${mutedTextClass}`}>
                {t("landing_brand_subtitle", "Racket Sports Intelligence")}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-4 lg:flex xl:gap-6">
            {links.map((item) => {
              const isActive = activeHash === item.href.substring(1);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="group relative py-1 text-sm font-medium"
                >
                  <span className={`transition-colors duration-300 ${navLinkClass(isActive)}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full bg-gradient-to-r from-[#20c5b5] to-[#ef4343]"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                  <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 rounded-full bg-gradient-to-r from-[#20c5b5] to-[#ef4343] transition-all duration-300 group-hover:w-full" />
                </a>
              );
            })}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden items-center gap-1 lg:flex lg:gap-2">
            <LanguageSwitcher />
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 rounded-full transition-all duration-300 sm:h-9 sm:w-9 ${isTransparent
                    ? "text-white hover:bg-white/10"
                    : isDark
                      ? "text-gray-300 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                onClick={onToggleTheme}
              >
                {isDark ? <SunMedium className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <MoonStar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                variant="ghost"
                className={`rounded-full px-3 text-sm transition-all duration-300 sm:px-4 ${isTransparent
                    ? "text-white"
                    : isDark
                      ? "text-gray-300"
                      : "text-gray-700"
                  }`}
              >
                <Link to="/login">{t("landing_login", "Login")}</Link>
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                asChild
                className={`rounded-full px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg sm:px-5 ${buttonVariantClass("primary")}`}
              >
                <Link to="/signup">{t("landing_start_free", "Start Free")}</Link>
              </Button>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-1 lg:hidden">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 rounded-full transition-all duration-300 ${isTransparent
                    ? "text-white hover:bg-white/10"
                    : isDark
                      ? "text-gray-300 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                onClick={onToggleTheme}
              >
                {isDark ? <SunMedium className="h-3.5 w-3.5" /> : <MoonStar className="h-3.5 w-3.5" />}
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 rounded-full transition-all duration-300 ${isTransparent
                    ? "text-white hover:bg-white/10"
                    : isDark
                      ? "text-gray-300 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                onClick={() => setOpen(!open)}
              >
                <AnimatePresence mode="wait">
                  {open ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu className="h-3.5 w-3.5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`mx-auto mt-2 overflow-hidden rounded-xl border shadow-xl backdrop-blur-xl lg:hidden ${mobileMenuClass}`}
            >
              <div className="flex flex-col p-2">
                {links.map((item, idx) => {
                  const isActive = activeHash === item.href.substring(1);
                  return (
                    <motion.a
                      key={item.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className={`rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 ${mobileLinkClass(isActive)}`}
                    >
                      {item.label}
                    </motion.a>
                  );
                })}
              </div>
              <div className={`border-t p-3 ${isDark ? "border-gray-800" : "border-gray-200"}`}>
                <LanguageSwitcher />
              </div>
              <div className="grid grid-cols-2 gap-2 p-3 pt-0">
                <Button
                  asChild
                  variant="outline"
                  className={`rounded-lg text-sm ${isDark
                      ? "border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  <Link to="/login" onClick={() => setOpen(false)}>{t("landing_login", "Login")}</Link>
                </Button>
                <Button
                  asChild
                  className="rounded-lg bg-gradient-to-r from-[#20c5b5] to-[#ef4343] text-sm text-white hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <Link to="/signup" onClick={() => setOpen(false)}>{t("landing_start_free", "Start Free")}</Link>
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
